import { OutreachAutomationType } from "../types";

export enum WizardStep {
  source = "source",
  configure = "configure",
  template = "template",
  settings = "settings",
}

export const STEP_ORDER: WizardStep[] = [
  WizardStep.source,
  WizardStep.configure,
  WizardStep.template,
  WizardStep.settings,
];

export const STEP_TITLES: Record<WizardStep, string> = {
  [WizardStep.source]: "Source",
  [WizardStep.configure]: "Configure",
  [WizardStep.template]: "Template",
  [WizardStep.settings]: "Settings",
};

export function getActiveStep(pathname: string): number {
  const segment = pathname.split("/").pop();
  const index = STEP_ORDER.findIndex((s) => s === segment);
  return index >= 0 ? index : 0;
}

/* ── Shared labels & name generation ─────────────────────────────── */

export const SOURCE_TYPE_LABELS: Record<OutreachAutomationType, string> = {
  bento_database: "Bento Brand Database",
  instagram_tracked_creators: "Instagram Tracked Creators",
  google_places: "Local Business Search",
};

const MAX_NAME_LENGTH = 80;

function truncateName(name: string): string {
  if (name.length <= MAX_NAME_LENGTH) return name;
  return name.slice(0, MAX_NAME_LENGTH - 1).trimEnd() + "\u2026";
}

export function generateDefaultName(
  sourceType: OutreachAutomationType,
  config: Record<string, any> | null | undefined,
  categoryNames?: string[],
): string {
  if (!config) return SOURCE_TYPE_LABELS[sourceType];

  let name: string;

  if (sourceType === "google_places") {
    const queries: string[] = config.search_queries || [];
    const geopoints = config.geopoints || [];
    if (queries.length > 0 && geopoints.length > 0) {
      try {
        const first =
          typeof geopoints[0] === "string"
            ? JSON.parse(geopoints[0])
            : geopoints[0];
        const locLabel = first.label || first.name || "";
        if (locLabel) {
          const suffix =
            geopoints.length > 1 ? ` + ${geopoints.length - 1} more` : "";
          name = `${queries[0]} in ${locLabel}${suffix}`;
          return truncateName(name);
        }
      } catch {
        /* fall through */
      }
    }
    name = queries.join(", ") || "Local Business Search";
    return truncateName(name);
  }

  if (sourceType === "instagram_tracked_creators") {
    const usernames: string[] = config.instagram_usernames || [];
    if (usernames.length === 1) {
      name = `Tracking @${usernames[0]}`;
    } else if (usernames.length > 1) {
      name = `Tracking @${usernames[0]} + ${usernames.length - 1} more`;
    } else {
      name = "Instagram Creator Tracking";
    }
    return truncateName(name);
  }

  if (sourceType === "bento_database") {
    const params = config.search_parameters || {};
    const query = params.query || "";
    const names = categoryNames ?? [];

    const categoryPart =
      names.length > 2
        ? `${names[0]}, ${names[1]}, etc.`
        : names.length > 0
          ? names.join(", ")
          : "";

    if (categoryPart && query) {
      name = `${categoryPart} (${query})`;
    } else if (categoryPart) {
      name = categoryPart;
    } else if (query) {
      name = query;
    } else {
      name = "Bento Brand Outreach";
    }
    return truncateName(name);
  }

  return "New Automation";
}
